import React from 'react'
import { sdk } from '@/lib/sdk'
import type { MemberField } from '@/lib/sdk-official/resources/member-import'
import type { AudienceFieldCondition, AudienceFieldOperator } from '@/types/campaigns'

/** F-156: only choice fields pick an audience. */
export const AUDIENCE_FIELD_TYPES = ['SELECT', 'MULTI_SELECT', 'BOOLEAN'] as const
export type AudienceFieldType = (typeof AUDIENCE_FIELD_TYPES)[number]

export const OPERATORS: Record<AudienceFieldType, AudienceFieldOperator[]> = {
  SELECT: ['oneOf'],
  MULTI_SELECT: ['containsAny', 'containsAll'],
  BOOLEAN: ['isTrue', 'isFalse'],
}

export const isAudienceType = (type: string): type is AudienceFieldType => (AUDIENCE_FIELD_TYPES as readonly string[]).includes(type)

/** A condition the API can take: a field, and an option unless it is a yes/no field. */
export const conditionIsComplete = (c: AudienceFieldCondition): boolean =>
  Boolean(c.fieldId || c.key) && (c.operator === 'isTrue' || c.operator === 'isFalse' || c.values.length > 0)

/**
 * An option removed from its field no longer counts (the API's `optionRemoved`) and would be refused on save,
 * so it is left out of what is sent; with every option gone the condition is incomplete and the form says so.
 */
export const pruneRemovedOptions = (list: AudienceFieldCondition[] | undefined, known: MemberField[]): AudienceFieldCondition[] =>
  (list ?? []).map((c) => {
    const field = known.find((f) => f.id === c.fieldId || (!c.fieldId && f.key === c.key))
    if (!field || (field.type !== 'SELECT' && field.type !== 'MULTI_SELECT')) return c
    const options = new Set((field.options ?? []).map((o) => o.value))
    return { ...c, values: c.values.filter((v) => options.has(v)) }
  })

/** What goes in `audience.fields`: the id when known (the key otherwise), the operator and the values. */
export const conditionsToApi = (list: AudienceFieldCondition[] | undefined, completeOnly = false) =>
  (list ?? [])
    .filter((c) => !completeOnly || conditionIsComplete(c))
    .map((c) => ({
      ...(c.fieldId ? { fieldId: c.fieldId } : { key: c.key }),
      operator: c.operator,
      values: c.operator === 'isTrue' || c.operator === 'isFalse' ? [] : c.values,
    }))

/**
 * F-156: the community's fields a condition may use, read once: SELECT, MULTI_SELECT and BOOLEAN, without
 * the ones the member page never shows ("Nunca": sensitive data never picks a mailing list). `all` keeps
 * every field, so a saved condition on a field that closed still shows its name.
 */
export const useAudienceFields = (enabled: boolean) => {
  const [all, setAll] = React.useState<MemberField[]>([])
  const [noAccess, setNoAccess] = React.useState(false)
  React.useEffect(() => {
    if (!enabled) return
    let alive = true
    sdk.memberFields.list()
      .then((list) => { if (alive) setAll(Array.isArray(list) ? list : []) })
      .catch((e) => { if (alive) { setAll([]); if (e?.response?.status === 403) setNoAccess(true) } })
    return () => { alive = false }
  }, [enabled])
  const usable = React.useMemo(() => all.filter((f) => isAudienceType(f.type) && f.profileVisibility !== 'NEVER'), [all])
  return { all, usable, noAccess }
}
